import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useI18n } from "../context/I18nContext";
import { errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError(t("rp.short"));
    if (pw !== pw2) return setError(t("rp.mismatch"));
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success(t("rp.success"));
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background" data-testid="reset-page">
      <div className="w-full max-w-sm border p-8">
        <div className="flex items-center gap-2 text-primary mb-4">
          <KeyRound size={16} />
          <span className="text-[10px] font-bold uppercase tracking-widest">MFIS Supply-Chain</span>
        </div>
        <h1 className="text-2xl font-black tracking-tighter mb-1">{t("rp.title")}</h1>
        <p className="text-xs text-muted-foreground mb-6">{t("rp.subtitle")}</p>
        {!token && (
          <div className="text-xs text-destructive border border-destructive/40 bg-destructive/5 p-2 mb-4">
            {t("rp.notoken")}
          </div>
        )}
        {done ? (
          <div className="flex items-center gap-2 text-sm border border-emerald-500/40 bg-emerald-500/5 p-3 text-emerald-700" data-testid="rp-success">
            <CheckCircle2 size={16} /> {t("rp.success")}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="rp-pw" className="text-xs font-bold uppercase tracking-widest">{t("rp.new")}</Label>
              <Input id="rp-pw" type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
                data-testid="rp-new-input" className="rounded-none" minLength={8} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rp-pw2" className="text-xs font-bold uppercase tracking-widest">{t("rp.confirm")}</Label>
              <Input id="rp-pw2" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)}
                data-testid="rp-confirm-input" className="rounded-none" minLength={8} />
            </div>
            {error && <div className="text-xs text-destructive border border-destructive/40 bg-destructive/5 p-2" data-testid="rp-error">{error}</div>}
            <Button type="submit" disabled={busy || !token} data-testid="rp-submit-button"
              className="w-full rounded-none font-bold uppercase tracking-widest">
              {t("rp.submit")}
            </Button>
          </form>
        )}
        <Link to="/login" className="inline-block mt-6 text-[11px] font-bold uppercase tracking-widest text-muted-foreground hover:text-primary">
          {t("fp.back")}
        </Link>
      </div>
    </div>
  );
}
